import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download, CheckCircle, AlertCircle, Loader, Building2 } from 'lucide-react';

import Button from '../../components/ui/Button';
import useImportStatusPolling from '../../hooks/useImportStatusPolling';
import {
  Container,
  StickyHeader,
  Title,
  StepIndicator,
  StepDot,
  StepLine,
  StepLabel,
  Card,
  DropZone,
  DropText,
  FileName,
  ErrorList,
  ErrorItem,
  SummaryGrid,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  TagList,
  Tag,
  Footer,
  HistoryTable,
  StatusBadge,
  TargetBanner,
  SectionTitle,
} from '../../components/import/ImportStyles';
import {
  getAssetImportTemplate,
  validateAssetImport,
  executeAssetImport,
  getAssetImportStatus,
  getAssetImportHistory,
} from '../../services/assetImportService';
import { useAuth } from '../../context/AuthContext';

export default function AssetImportPage() {
  const { t } = useTranslation();
  const { accessibleTenants, activeTenantId, canSwitchTenant } = useAuth();
  const activeTenant = accessibleTenants.find((tn) => tn.id === activeTenantId);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [history, setHistory] = useState([]);
  const fileInputRef = useRef(null);
  const { importStatus, statusLost, startPolling, retryPolling, resetPolling } = useImportStatusPolling(getAssetImportStatus);

  // Load import history on mount
  useEffect(() => {
    getAssetImportHistory({ pageNumber: 1, pageSize: 10 })
      .then((res) => setHistory(res.data || []))
      .catch(() => {});
  }, [importStatus?.status]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const res = await getAssetImportTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'flexbee-asset-import-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Handled by interceptor
    }
  }, []);

  const handleFileSelect = useCallback((selectedFile) => {
    if (selectedFile && selectedFile.name.endsWith('.xlsx')) {
      setFile(selectedFile);
      setValidation(null);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleValidate = useCallback(async () => {
    if (!file) return;
    setValidating(true);
    try {
      const res = await validateAssetImport(file);
      setValidation(res.data);
      if (res.data.errors?.length === 0) {
        setStep(3);
      }
    } catch {
      // Handled by interceptor
    } finally {
      setValidating(false);
    }
  }, [file]);

  const handleExecute = useCallback(async () => {
    if (!validation?.importId) return;
    setExecuting(true);
    try {
      await executeAssetImport(validation.importId, file?.name || '', validation.totalRows);
      setStep(4);

      startPolling(validation.importId);
    } catch {
      // Handled by interceptor
    } finally {
      setExecuting(false);
    }
  }, [validation, file, startPolling]);

  const handleReset = useCallback(() => {
    setStep(1);
    setFile(null);
    setValidation(null);
    resetPolling();
  }, [resetPolling]);

  // Sunucu satır mesajı bir çeviri ANAHTARIDIR ("assetImport.error.invalidDateFormat");
  // çevirisi responseErrors altında durur (Ek İzin içe aktarmasıyla aynı desen). Eskiden
  // ham mesajı doğrudan t()'ye veriyor, kökte bulamıyor ve müşteriye anahtarı gösteriyordu.
  // "assetImport.error.unexpected: <detay>" gibi son ekli mesajda yalnız ':' öncesi çevrilir.
  const translateRowMessage = useCallback((message) => {
    if (!message) return message;
    const sepIdx = message.indexOf(': ');
    const key = sepIdx === -1 ? message : message.slice(0, sepIdx);
    const rest = sepIdx === -1 ? '' : message.slice(sepIdx);
    return t(`responseErrors.${key}`, { defaultValue: key }) + rest;
  }, [t]);

  const steps = [
    { label: t('assetImport.step1') },
    { label: t('assetImport.step2') },
    { label: t('assetImport.step3') },
    { label: t('assetImport.step4') },
  ];

  return (
    <Container>
      <StickyHeader>
        <Title>{t('assetImport.title')}</Title>
        {activeTenant && (
          <TargetBanner>
            <Building2 size={18} />
            <span>
              {t(
                canSwitchTenant ? 'assetImport.targetTenantInfoSwitchable' : 'assetImport.targetTenantInfo',
                { tenant: activeTenant.name }
              )}
            </span>
          </TargetBanner>
        )}
        <StepIndicator>
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <StepLine $completed={step > i} />}
              <StepDot $active={step === i + 1} $completed={step > i + 1}>
                {step > i + 1 ? '✓' : i + 1}
              </StepDot>
              <StepLabel $active={step === i + 1}>{s.label}</StepLabel>
            </React.Fragment>
          ))}
        </StepIndicator>
      </StickyHeader>

      {/* Step 1: Download Template */}
      {step === 1 && (
        <Card>
          <SectionTitle>{t('assetImport.downloadTemplate')}</SectionTitle>
          <p style={{ fontFamily: 'Nunito Sans', fontSize: 14, color: '#667085', marginBottom: 16 }}>
            {t('assetImport.downloadTemplateDesc')}
          </p>
          <Button onClick={handleDownloadTemplate}>
            <Download size={16} style={{ marginRight: 8 }} />
            {t('assetImport.downloadButton')}
          </Button>
          <Footer>
            <div />
            <Button onClick={() => setStep(2)}>{t('assetImport.next')}</Button>
          </Footer>
        </Card>
      )}

      {/* Step 2: Upload & Validate */}
      {step === 2 && (
        <Card>
          <SectionTitle>{t('assetImport.uploadFile')}</SectionTitle>

          <DropZone
            $dragOver={dragOver}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <Upload size={32} color="#667085" />
            <DropText>
              {file ? (
                <FileName>{file.name}</FileName>
              ) : (
                t('assetImport.dropzone')
              )}
            </DropText>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files[0])}
            />
          </DropZone>

          {validation && (
            <>
              <SummaryGrid style={{ marginTop: 20 }}>
                <SummaryCard $color="#F0F9FF">
                  <SummaryValue>{validation.totalRows}</SummaryValue>
                  <SummaryLabel>{t('assetImport.totalRows')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#F0FDF4">
                  <SummaryValue>{validation.newRows}</SummaryValue>
                  <SummaryLabel>{t('assetImport.newRows')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#FFFBEB">
                  <SummaryValue>{validation.updateRows}</SummaryValue>
                  <SummaryLabel>{t('assetImport.updateRows')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#FEF2F2">
                  <SummaryValue>{validation.errors?.length || 0}</SummaryValue>
                  <SummaryLabel>{t('assetImport.errorCount')}</SummaryLabel>
                </SummaryCard>
              </SummaryGrid>

              <SummaryGrid>
                <SummaryCard $color="#EEF2FF">
                  <SummaryValue>{validation.assignmentCount ?? 0}</SummaryValue>
                  <SummaryLabel>{t('assetImport.summary.assignments')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#FDF4FF">
                  <SummaryValue>{validation.reassignmentCount ?? 0}</SummaryValue>
                  <SummaryLabel>{t('assetImport.summary.reassignments')}</SummaryLabel>
                </SummaryCard>
              </SummaryGrid>

              {validation.newCategories?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <SectionTitle>{t('assetImport.summary.newCategories')}</SectionTitle>
                  <TagList>
                    {validation.newCategories.map((cat) => (
                      <Tag key={cat}>{cat}</Tag>
                    ))}
                  </TagList>
                </div>
              )}

              {validation.errors?.length > 0 && (
                <ErrorList>
                  <SectionTitle>{t('assetImport.errors')}</SectionTitle>
                  {validation.errors.map((err, i) => (
                    <ErrorItem key={i} $severity="error">
                      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{t('assetImport.row')} {err.row}: {err.column} — {translateRowMessage(err.message)}</span>
                    </ErrorItem>
                  ))}
                </ErrorList>
              )}

              {validation.warnings?.length > 0 && (
                <ErrorList>
                  <SectionTitle>{t('assetImport.warnings')}</SectionTitle>
                  {validation.warnings.map((warn, i) => (
                    <ErrorItem key={i} $severity="warning">
                      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{t('assetImport.row')} {warn.row}: {warn.column} — {translateRowMessage(warn.message)}</span>
                    </ErrorItem>
                  ))}
                </ErrorList>
              )}
            </>
          )}

          <Footer>
            <Button variant="ghost" onClick={() => setStep(1)}>{t('assetImport.back')}</Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="outline"
                onClick={handleValidate}
                disabled={!file || validating}
              >
                {validating ? <Loader size={16} className="spin" /> : null}
                {t('assetImport.validate')}
              </Button>
              {validation && validation.errors?.length === 0 && (
                <Button onClick={() => setStep(3)}>
                  {t('assetImport.next')}
                </Button>
              )}
            </div>
          </Footer>
        </Card>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && validation && (
        <Card>
          <SectionTitle>{t('assetImport.confirmTitle')}</SectionTitle>
          <SummaryGrid>
            <SummaryCard $color="#F0FDF4">
              <SummaryValue>{validation.newRows}</SummaryValue>
              <SummaryLabel>{t('assetImport.willInsert')}</SummaryLabel>
            </SummaryCard>
            <SummaryCard $color="#FFFBEB">
              <SummaryValue>{validation.updateRows}</SummaryValue>
              <SummaryLabel>{t('assetImport.willUpdate')}</SummaryLabel>
            </SummaryCard>
            <SummaryCard $color="#EEF2FF">
              <SummaryValue>{validation.assignmentCount ?? 0}</SummaryValue>
              <SummaryLabel>{t('assetImport.summary.assignments')}</SummaryLabel>
            </SummaryCard>
            <SummaryCard $color="#FDF4FF">
              <SummaryValue>{validation.reassignmentCount ?? 0}</SummaryValue>
              <SummaryLabel>{t('assetImport.summary.reassignments')}</SummaryLabel>
            </SummaryCard>
          </SummaryGrid>

          {validation.newCategories?.length > 0 && (
            <p style={{ fontFamily: 'Nunito Sans', fontSize: 13, color: '#667085', marginBottom: 8 }}>
              {t('assetImport.newCategoriesNote', { count: validation.newCategories.length })}
            </p>
          )}

          <Footer>
            <Button variant="ghost" onClick={() => setStep(2)}>{t('assetImport.back')}</Button>
            <Button onClick={handleExecute} disabled={executing}>
              {executing ? <Loader size={16} /> : <CheckCircle size={16} style={{ marginRight: 8 }} />}
              {t('assetImport.startImport')}
            </Button>
          </Footer>
        </Card>
      )}

      {/* Step 4: Progress & Result */}
      {step === 4 && (
        <Card>
          {!importStatus || importStatus.status === 'queued' || importStatus.status === 'processing' ? (
            statusLost ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <AlertCircle size={40} color="#D97706" />
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 16, fontWeight: 600, marginTop: 16 }}>
                  {t('assetImport.statusUnavailable')}
                </p>
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 13, color: '#667085' }}>
                  {t('assetImport.statusUnavailableDesc')}
                </p>
                <Button variant="secondary" onClick={retryPolling}>
                  {t('assetImport.statusRetry')}
                </Button>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Loader size={40} color="#0291EF" className="spin" />
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 16, fontWeight: 600, marginTop: 16 }}>
                  {t('assetImport.processing')}
                </p>
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 13, color: '#667085' }}>
                  {t('assetImport.processingDesc')}
                </p>
              </div>
            )
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                {importStatus.status === 'completed' ? (
                  <CheckCircle size={48} color="#16a34a" />
                ) : (
                  <AlertCircle size={48} color="#DC2626" />
                )}
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 18, fontWeight: 700, marginTop: 12 }}>
                  {importStatus.status === 'completed' ? t('assetImport.completed') : t('assetImport.failed')}
                </p>
              </div>

              <SummaryGrid>
                <SummaryCard $color="#F0F9FF">
                  <SummaryValue>{importStatus.totalRows}</SummaryValue>
                  <SummaryLabel>{t('assetImport.totalRows')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#F0FDF4">
                  <SummaryValue>{importStatus.insertedRows}</SummaryValue>
                  <SummaryLabel>{t('assetImport.inserted')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#FFFBEB">
                  <SummaryValue>{importStatus.updatedRows}</SummaryValue>
                  <SummaryLabel>{t('assetImport.updated')}</SummaryLabel>
                </SummaryCard>
                <SummaryCard $color="#FEF2F2">
                  <SummaryValue>{importStatus.errorRows}</SummaryValue>
                  <SummaryLabel>{t('assetImport.errorCount')}</SummaryLabel>
                </SummaryCard>
              </SummaryGrid>

              {importStatus.errors?.length > 0 && (
                <ErrorList>
                  <SectionTitle>{t('assetImport.errors')}</SectionTitle>
                  {importStatus.errors.map((err, i) => (
                    <ErrorItem key={i} $severity="error">
                      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{t('assetImport.row')} {err.row}: {err.column} — {err.message}</span>
                    </ErrorItem>
                  ))}
                </ErrorList>
              )}

              <Footer>
                <div />
                <Button onClick={handleReset}>{t('assetImport.newImport')}</Button>
              </Footer>
            </>
          )}
        </Card>
      )}

      {/* Import History */}
      {history.length > 0 && (
        <Card style={{ marginTop: 32 }}>
          <SectionTitle>{t('assetImport.history')}</SectionTitle>
          <HistoryTable>
            <thead>
              <tr>
                <th>{t('assetImport.fileName')}</th>
                <th>{t('assetImport.status')}</th>
                <th>{t('assetImport.totalRows')}</th>
                <th>{t('assetImport.inserted')}</th>
                <th>{t('assetImport.updated')}</th>
                <th>{t('assetImport.errorCount')}</th>
                <th>{t('assetImport.date')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={item.id}>
                  <td>{item.fileName}</td>
                  <td><StatusBadge $status={item.status}>{t(`assetImport.status_${item.status}`)}</StatusBadge></td>
                  <td>{item.totalRows}</td>
                  <td>{item.insertedRows}</td>
                  <td>{item.updatedRows}</td>
                  <td>{item.errorRows}</td>
                  <td>{new Date(item.createDate).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </HistoryTable>
        </Card>
      )}
    </Container>
  );
}
